import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  mergeClasses,
  Option,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import {
  adminCreateSurveyTemplate,
  adminUpdateSurveyTemplate,
} from "apps/project-iq/api";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { useEffect, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { useEditSurveyTemplateDialogStyles } from "./EditSurveyTemplateDialog.styles";
import {
  IEditSurveyTemplateDialogProps,
  SURVEY_TEMPLATE_CATEGORIES,
} from "./EditSurveyTemplateDialog.types";

export const EditSurveyTemplateDialog = ({
  open,
  template,
  onSave,
  onDismiss,
}: IEditSurveyTemplateDialogProps): JSX.Element => {
  const styles = useEditSurveyTemplateDialogStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();

  const [name, setName] = useState<string>("");
  const [category, setCategory] = useState<string>(
    SURVEY_TEMPLATE_CATEGORIES[0],
  );
  const [overview, setOverview] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [whenToUse, setWhenToUse] = useState<string>("");
  const [questionBankUrl, setQuestionBankUrl] = useState<string>("");
  const [guideUrl, setGuideUrl] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setCategory(template.category);
      setOverview(template.overview ?? "");
      setDescription(template.description);
      setWhenToUse(template.whenToUse);
      setQuestionBankUrl(template.questionBankUrl ?? "");
      setGuideUrl(template.guideUrl ?? "");
      setIsActive(template.isActive ?? true);
      setFieldErrors({});
    } else {
      setName("");
      setCategory(SURVEY_TEMPLATE_CATEGORIES[0]);
      setOverview("");
      setDescription("");
      setWhenToUse("");
      setQuestionBankUrl("");
      setGuideUrl("");
      setIsActive(true);
      setFieldErrors({});
    }
    setIsSaving(false);
  }, [template, open]);

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = "Template name is required";
    if (!category.trim()) errors.category = "Category is required";
    return errors;
  };

  const handleSave = async (): Promise<void> => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setIsSaving(true);
    try {
      const data = {
        name: name.trim(),
        category,
        overview: overview.trim(),
        description: description.trim(),
        whenToUse: whenToUse.trim(),
        questionBankUrl: questionBankUrl.trim(),
        guideUrl: guideUrl.trim(),
        isActive,
      };
      if (template?.id) {
        await adminUpdateSurveyTemplate(template.id, data);
      } else {
        await adminCreateSurveyTemplate(data);
      }
      onSave();
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        general: "Save failed. Please try again.",
      }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface
        className={mergeClasses(
          contentStyles.dialogSurface,
          styles.dialogSurface,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
              />
            }
          >
            {template ? "Edit Survey Template" : "Add Survey Template"}
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <div className={contentStyles.formContainer}>
              {isSaving && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving template..." />
                </div>
              )}
              {fieldErrors.general && (
                <div role="alert" className={styles.errorAlert}>
                  {fieldErrors.general}
                </div>
              )}
              <Field
                label="Template Name"
                required
                validationState={fieldErrors.name ? "error" : "none"}
                validationMessage={fieldErrors.name}
                className={styles.dialogField}
              >
                <Input
                  value={name}
                  onChange={(_, data) => {
                    setName(data.value);
                    if (fieldErrors.name)
                      setFieldErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  placeholder="Template name"
                />
              </Field>
              <Field
                label="Category"
                required
                validationState={fieldErrors.category ? "error" : "none"}
                validationMessage={fieldErrors.category}
                className={styles.dialogField}
              >
                <Dropdown
                  value={category}
                  onOptionSelect={(_, data) =>
                    setCategory(data.optionValue ?? "")
                  }
                >
                  {SURVEY_TEMPLATE_CATEGORIES.map((cat) => (
                    <Option key={cat} value={cat}>
                      {cat}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Overview" className={styles.dialogField}>
                <Textarea
                  value={overview}
                  onChange={(_, data) => setOverview(data.value)}
                  placeholder="Brief overview of this template"
                  rows={3}
                />
              </Field>
              <Field label="Description" className={styles.dialogField}>
                <Textarea
                  value={description}
                  onChange={(_, data) => setDescription(data.value)}
                  placeholder="Detailed description"
                  rows={3}
                />
              </Field>
              <Field label="When to Use" className={styles.dialogField}>
                <Textarea
                  value={whenToUse}
                  onChange={(_, data) => setWhenToUse(data.value)}
                  placeholder="When should this template be used?"
                  rows={3}
                />
              </Field>
              <Field label="Question Bank URL" className={styles.dialogField}>
                <Input
                  value={questionBankUrl}
                  onChange={(_, data) => setQuestionBankUrl(data.value)}
                  placeholder="https://..."
                />
              </Field>
              <Field label="Guide URL" className={styles.dialogField}>
                <Input
                  value={guideUrl}
                  onChange={(_, data) => setGuideUrl(data.value)}
                  placeholder="https://..."
                />
              </Field>
              <CustomToggle
                checked={isActive}
                onChange={setIsActive}
                label={isActive ? "Active" : "Inactive"}
              />
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip="Save"
            dimmed={!name.trim()}
            disabled={isSaving}
            onClick={() => void handleSave()}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
